/**
 * Helpers for club-written bodies that may be plain text or the formatting
 * bar's HTML. Sanitising is not done here: the server does it on save and the
 * website again on render.
 */

/** Same test the server and the website use. */
export function isRichText(value: unknown): value is string {
  return typeof value === 'string' && /<(p|br|strong|em|u|s|a|ul|ol|li|h3|h4|blockquote)\b/i.test(value);
}

const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * A plain body as editor HTML: blank lines become paragraphs and single line
 * breaks stay line breaks, which is how the website shows plain text. Dropping
 * plain text straight into the editor ran its paragraphs together, and the
 * first bit of formatting then saved them that way.
 */
export function plainToHtml(text: string): string {
  const t = text.replace(/\r\n?/g, '\n').trim();
  if (!t) return '';
  return t.split(/\n{2,}/).map(p => `<p>${escapeHtml(p).replace(/\n/g, '<br>')}</p>`).join('');
}

export function toEditorHtml(value: string): string {
  return isRichText(value) ? value : plainToHtml(value || '');
}

/** Text for a one-line admin preview, whichever form the body is in. */
export function previewText(value: string): string {
  if (!isRichText(value)) return value;
  const spaced = value.replace(/<br\s*\/?>|<\/(p|li|h3|h4|blockquote)>/gi, '$& ');
  return (new DOMParser().parseFromString(spaced, 'text/html').body.textContent || '').replace(/\s+/g, ' ').trim();
}

/** Does this body have any words in it, whichever form it is in? */
export function hasText(value: string): boolean {
  return previewText(value || '').trim().length > 0;
}

/**
 * The formatting bar is on every plan (Gideon, 2026-09-29). GrassrootsFC keeps
 * it to Club and Pro; the parameters stay so the call sites match theirs. The
 * server sanitises on save for every plan either way.
 */
export function richTextAllowed(_plan?: string, _features?: unknown): boolean {
  return true;
}
